import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';
import { DOC_CSS } from './docCss';

export { normalizeCompanyContext } from './companyContract';

// Configured once at module load. GFM adds support for tables and strikethrough
// — without `.use(gfm)`, turndown silently drops tables, which is the loss we're
// trying to prevent on the AI regeneration round-trip.
const turndownService = new TurndownService({
  headingStyle: 'atx',           // # heading rather than === underline
  codeBlockStyle: 'fenced',
  emDelimiter: '*',
  bulletListMarker: '-',
});
turndownService.use(gfm);

/**
 * The date a draft document prints (meeting-details table, signature block).
 *
 * Defaults to TODAY — which is what every template hardcoded before the draft
 * modals grew a date picker. A value from the picker arrives as ISO
 * "YYYY-MM-DD" and is formatted here; text that is already in display form
 * ("5 August 2026", e.g. coming back from the AI pipeline) passes through.
 *
 * Parsed from parts rather than via Date.parse so an ISO string is read as a
 * LOCAL calendar date — "2026-07-29" must never render as the 28th.
 */
export function formatDocumentDate(value) {
  const raw = String(value || '').trim();
  const asDisplay = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  if (!raw) return asDisplay(new Date());
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!m) return raw;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? raw : asDisplay(d);
}

export function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function stripCodeFences(value = '') {
  return String(value)
    .replace(/^```html\s*/i, '')
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();
}

export function htmlToPlainText(html = '') {
  const text = String(html || '').trim();
  if (!text) return '';

  if (typeof window === 'undefined' || !window.document) {
    // Remove style tags and CSS content first, preserve paragraph structure
    const withoutStyles = text
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    return withoutStyles;
  }

  // Remove style tags and CSS content, preserve paragraph structure
  const withoutStyles = text
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, ' ');

  const node = document.createElement('div');
  node.innerHTML = withoutStyles;
  let plainText = (node.innerText || node.textContent || '')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s{2,}/g, ' ')
    .trim();

  // Add proper line breaks and formatting for better readability
  plainText = plainText
    // Add spacing after company header lines (patterns with pipes and company info)
    .replace(/^([A-Z][A-Z\s&]+(?:LIMITED|PRIVATE|LLP|PUBLIC|OPC|SECTION\s8|NGO|TRUST|SOCIETY|PARTNERSHIP|ASSOCIATION|FIRM|COMPANY)[^\n]*\|[^\n]*)/gm, '$1\n\n')
    // Add spacing after CIN/LLPIN lines
    .replace(/(CIN \/ LLPIN\s*:\s*[A-Z0-9]+[^\n]*\|)/g, '$1\n\n')
    // Add spacing after capital information
    .replace(/(Auth\.\s*Capital\s*:\s*[^|\n]*\|[^|\n]*Paid-Up\s*Capital\s*:\s*[^|\n]*)/g, '$1\n\n')
    // Add spacing after CERTIFIED TRUE COPY header
    .replace(/(CERTIFIED TRUE COPY OF THE RESOLUTION[^\n]*)/g, '$1\n\n')
    // Add spacing after location details
    .replace(/(HELD AT THE REGISTERED OFFICE[^\n]*\d+[^\n]*)/g, '$1\n\n')
    // Add spacing after the certification line's date/time tail. Matches both a
    // real date ("…ON Thursday, 23 July 2026 AT 11:00 A.M.") and the [TIME]
    // placeholder that survives when no time was supplied. The old rule keyed
    // on a literal "ON [Day, Date] AT [Time]." that renderers no longer emit.
    .replace(/(ON [^\n]{4,40} AT (?:\[TIME\]|[\d: ]+[AP]\.M\.))\.?/gi, '$1\n\n')
    // Add spacing after company info blocks
    .replace(/(Company\s*:\s*[A-Z][A-Z\s&]+(?:LIMITED|PRIVATE|LLP|PUBLIC|OPC)[^\n]*\nCIN \/ LLPIN\s*:\s*[A-Z0-9]+[^\n]*\nDate of Meeting\s*:\s*[^\n]*)/g, '$1\n\n')
    // Format section headers with proper spacing
    .replace(/\*\*(Venue|Time|Mode of Meeting)\s*:\s*\*\*/g, '\n**$1 :**')
    // Add spacing before RESOLVED THAT clauses
    .replace(/(RESOLVED THAT)/g, '\n\n**$1**')
    // Add spacing after resolved clauses (before next section)
    .replace(/(with effect from [^.]*\.[^\n]*\n)/g, '$1\n\n')
    // Format the authorisation clause properly (house style: RESOLVED FURTHER THAT)
    .replace(/FURTHER\s*\n\s*\*\*RESOLVED THAT\*\*/g, '\n**RESOLVED FURTHER THAT**')
    // Add spacing after further resolved clauses
    .replace(/(be authorised to sign and submit the same\.)/g, '$1\n\n')
    // Format certification line with emphasis
    .replace(/(Certified to be (?:a )?true record)/gi, '\n\n--- $1 ---\n\n')
    // Add spacing before "For COMPANY NAME" signatures
    .replace(/(For\s+[A-Z][A-Z\s&]+(?:LIMITED|PRIVATE|LLP|PUBLIC|OPC))/g, '\n\n$1\n\n')
    // Add spacing between director signatures
    .replace(/([A-Z][a-z\s]+\s+[A-Z][a-z\s]+\s+(?:Managing\s+)?Director\s*DIN\s*:\s*\d+[^\n]*)/g, '$1\n\n')
    // Add spacing before date and place lines
    .replace(/(Date\s*:\s*[^\n]+)/g, '\n$1')
    .replace(/(Place\s*:\s*[^\n]+)/g, '\n$1')
    // Add spacing before final company identification line
    .replace(/([A-Z][A-Z\s&]+(?:LIMITED|PRIVATE|LLP|PUBLIC|OPC)[^\n]*\|[^|\n]*CIN[^|\n]*\|[^|\n]*)$/gm, '\n\n$1')
    // Clean up extra spacing around colons
    .replace(/\s*:\s*/g, ' : ')
    // Add proper spacing after periods at end of sentences
    .replace(/\.([A-Z])/g, '. $1')
    // Clean up excessive line breaks (limit to max 2 consecutive)
    .replace(/\n{3,}/g, '\n\n')
    // Remove leading/trailing whitespace on lines
    .replace(/^\s*\n/gm, '\n')
    .replace(/\n\s*$/gm, '\n')
    // Ensure proper spacing around legal phrases
    .replace(/(pursuant to Section \d+)/g, ' $1')
    .replace(/(read with Section \d+)/g, ' $1')
    .replace(/(under Section \d+)/g, ' $1');

  return plainText.trim();
}

/**
 * Substitute a filled-in value for a placeholder in rendered draft HTML.
 *
 * The renderer wraps every surviving [BRACKETED] token in
 * `<span class="doc-ph">` so it shows as an amber "still needs a value" chip.
 * A naive text swap replaces the token but leaves that wrapper behind, so the
 * user's OWN answer keeps the "unfilled" highlight — which is worse than no
 * highlight at all, because it flags completed fields as outstanding.
 *
 * So: drop the whole span when the placeholder it wraps is being filled, then
 * handle any unwrapped occurrences.
 *
 * @param {string} html         rendered draft HTML
 * @param {string} placeholder  e.g. "[NAME]"
 * @param {string} escapedValue replacement, ALREADY html-escaped by the caller
 */
export function fillPlaceholderInHtml(html, placeholder, escapedValue) {
  const literal = String(placeholder);
  const rxSafe = literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Attribute-order tolerant: the span may be re-serialised by the sanitiser.
  const wrapped = new RegExp(
    `<span[^>]*class="[^"]*doc-ph[^"]*"[^>]*>\\s*${rxSafe}\\s*</span>`,
    'g',
  );
  return String(html || '')
    .replace(wrapped, escapedValue)
    .split(literal)
    .join(escapedValue);
}

/**
 * Rendered draft HTML → clean plain text for a .TXT download or the clipboard.
 *
 * Deliberately NOT htmlToPlainText. That function "beautifies" by INJECTING
 * markdown (`RESOLVED THAT` → `**RESOLVED THAT**`) and rewriting spacing, which
 * is right for the on-screen/AI round-trip it feeds but produces a .txt full of
 * literal asterisks and `| CIN : … |` table pipes. It also emitted the footer
 * AND the watermark, giving every export a trailing
 * "ComplyV ComplyV".
 *
 * Here the rules are simply: block elements become line breaks, table cells
 * become " | " separated rows, decorative chrome is dropped, and no markup is
 * ever added.
 */
export function htmlToExportText(html = '') {
  const raw = String(html || '').trim();
  if (!raw) return '';

  let working = raw
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    // Decorative-only chrome: the watermark duplicates the footer verbatim.
    .replace(/<div[^>]*class="[^"]*doc-watermark[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '');

  if (typeof document !== 'undefined' && document.createElement) {
    const node = document.createElement('div');
    node.innerHTML = working;
    node.querySelectorAll('.doc-watermark').forEach((el) => el.remove());
    // Rows become one line; cells keep a visible separator so the meeting
    // table stays readable instead of running together.
    node.querySelectorAll('tr').forEach((tr) => {
      const cells = [...tr.children].map((td) => (td.textContent || '').trim()).filter(Boolean);
      tr.replaceWith(document.createTextNode(`\n${cells.join(' : ')}`));
    });
    node.querySelectorAll('p,div,h1,h2,h3,h4,h5,h6,li,hr,br').forEach((el) => {
      el.insertAdjacentText('afterend', '\n');
    });
    working = node.innerText || node.textContent || '';
  } else {
    working = working
      .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/t[dh]>/gi, ' : ')
      .replace(/<[^>]+>/g, '');
  }

  return working
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // Strip any markdown that survived from the model's own output.
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/(^|\s)#{1,6}\s+/g, '$1')
    .replace(/^\s*\|\s*-{2,}.*$/gm, '')
    .replace(/[ \t]{2,}/g, ' ')
    // Inline spans (e.g. the .doc-ph placeholder highlight) leave a space before
    // the punctuation that followed them — "AT [TIME] ." reads as a typo.
    .replace(/[ \t]+([.,;:!?])/g, '$1')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Convert rendered draft HTML into Markdown for the AI regeneration round-trip.
// Markdown preserves the structure that htmlToPlainText destroys — tables (via
// GFM `| col | col |\n|---|---|`), lists, headings, bold/italic — so when the
// AI sees the existing draft and regenerates, table-shaped sections survive.
// Modern LLMs are trained extensively on Markdown and reproduce it natively.
// Chrome the backend renderer REGENERATES on every draft — passing it to the AI
// makes the model reproduce letterhead/signature/footer text as body paragraphs
// (the "General statutory document" degradation). Selectors cover the template
// classes; the style heuristics cover the renderer's inline-styled equivalents.
const CHROME_SELECTORS = [
  'style', 'hr',
  '.doc-letterhead', '.doc-doc-title', '.doc-footer', '.doc-watermark', '.doc-sign-block',
].join(',');
// Inline-style signatures of the backend renderers' chrome (they emit no classes).
// Every one of these blocks is REGENERATED by the renderer on the next draft, so
// leaving any in the baseline gets it duplicated into the body:
//   letterhead      → border-bottom:3px double
//   footer          → border-top:1px solid #ccc
//   sign block      → grid-template-columns:1fr 1fr OR repeat(N,1fr) (both renderers use repeat())
//   doc title       → text-transform:uppercase … text-decoration:underline (document renderer)
//   certified box   → border:1px solid #000;padding:10px 14px (resolution renderer)
//   meeting table   → border-collapse:collapse;margin-bottom:16px (resolution renderer;
//                     AI-content tables come from marked and carry no inline styles)
// Each pattern must ALSO match the CSSOM-normalized form: after a hand-edit in
// the artifact panel we persist element.innerHTML, and browsers re-serialize
// style attributes — a space appears after every colon/semicolon and hex colors
// become rgb(r, g, b). Hence the \s* after separators and the #hex|rgb() alternates.
const CHROME_STYLE_PATTERNS = [
  'border-bottom:\\s*3px\\s+double',
  'border-top:\\s*1px\\s+solid\\s+(?:#ccc|rgb\\(204,\\s*204,\\s*204\\))',
  'grid-template-columns:\\s*(?:1fr\\s+1fr|repeat\\()',
  'text-transform:\\s*uppercase[^"\']*text-decoration:\\s*underline',
  'border:\\s*1px\\s+solid\\s+(?:#000|rgb\\(0,\\s*0,\\s*0\\));\\s*padding:\\s*10px\\s+14px',
  'border-collapse:\\s*collapse;\\s*margin-bottom:\\s*16px',
];
const CHROME_STYLE_RE = new RegExp(CHROME_STYLE_PATTERNS.map((p) => `(${p})`).join('|'), 'i');

export function htmlToMarkdown(html = '') {
  const text = String(html || '').trim();
  if (!text) return '';
  try {
    // Strip our renderer's outer chrome — letterhead / doc title / signature
    // block / footer get regenerated by the backend renderer, so passing them
    // to the AI both wastes tokens AND gets them duplicated into the body.
    const doc = new DOMParser().parseFromString(text, 'text/html');
    doc.querySelectorAll(CHROME_SELECTORS).forEach((el) => el.remove());
    // The AI-draft renderers emit the same chrome with inline styles, no classes
    // ([style] not div[style]: the meeting-details block is a <table>).
    doc.querySelectorAll('[style]').forEach((el) => {
      if (CHROME_STYLE_RE.test(el.getAttribute('style') || '')) el.remove();
    });
    return turndownService.turndown(doc.body.innerHTML);
  } catch (err) {
    // If parsing/turndown trips on malformed HTML, fall back to plain text so
    // the regeneration still works (just loses table structure).
    console.warn('[htmlToMarkdown] failed, falling back to plain text:', err?.message);
    return htmlToPlainText(html);
  }
}

export function coerceParagraphHtml(content = '') {
  const cleaned = stripCodeFences(content);
  if (!cleaned) return '';

  if (/<\/?[a-z][\s\S]*>/i.test(cleaned)) {
    return cleaned;
  }

  return cleaned
    .split(/\n\s*\n/)
    .filter(Boolean)
    .map((paragraph) => `<p style="margin:0 0 10px 0;line-height:1.8">${escapeHtml(paragraph).replace(/\n/g, '<br/>')}</p>`)
    .join('');
}

export function buildStandaloneHtml(title, bodyHtml) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(title || 'Draft Document')}</title>
  <style>${DOC_CSS}</style>
</head>
<body style="margin:0;background:#fff">
  <div class="doc-preview" style="max-width:820px;margin:0 auto">
    ${bodyHtml || ''}
  </div>
</body>
</html>`;
}

// Convert stored ISO date "YYYY-MM-DD" → readable "DD/MM/YYYY" for document templates.
export function fromIso(iso = '') {
  const s = String(iso || '').trim();
  if (!s) return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

export function getDraftSourceLabel(source = '', cacheHit = false) {
  if (cacheHit || source === 'cache') return 'Recent draft';
  if (source === 'ai-json') return 'Fresh AI draft';
  if (source === 'ai-fallback-normalized' || source === 'fallback') return 'Template fallback';
  return 'Draft prepared';
}

/**
 * Is this the same draft, when object identity cannot be trusted?
 *
 * The fill path updates two things: the draft on the message, and the artifact
 * the side panel is showing. Both were matched by `===`, which holds only while
 * they are literally the same object — true for a draft that arrived this
 * session, and FALSE for one restored from localStorage or reopened from
 * history, because those rebuild their draft objects from JSON.
 *
 * When identity breaks, the message updates and the panel does not: the toast
 * reports "Filled 9 details into the document" while the document on screen is
 * still the one before the fill. The user is told it worked and can see that it
 * did not.
 *
 * Kind + title + company is enough to identify a draft within one message —
 * a message never carries two drafts of the same kind and title for the same
 * company, and if it somehow did, both would deserve the same update anyway.
 */
export const isSameDraft = (a, b) => a === b || Boolean(
  a && b
  && a.kind === b.kind
  && String(a.title || '') === String(b.title || '')
  && String(a.companyCin || '') === String(b.companyCin || ''),
);

/**
 * Apply the fill form's answers to a draft, deterministically.
 *
 * Extracted from AgentPage and CopilotDock, which carried near-identical copies
 * of this loop. Two copies of the code that writes user answers into a
 * statutory document is one copy too many — and neither could be tested, being
 * buried inside a component. Everything the callers need to report what
 * happened comes back from here, so a receipt can never claim a value the
 * document did not actually take.
 *
 * @param   {object} draft   { html, plainText }
 * @param   {Object<string,string>} values  placeholder token → value
 * @returns {{ html, plainText, applied: string[], skipped: string[] }}
 *          `applied` = tokens found in the document and replaced.
 *          `skipped` = non-empty answers whose token was NOT in the document.
 */
export function applyPlaceholderFill(draft, values = {}) {
  const esc = (v) => String(v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  let html = String((draft && draft.html) || '');
  let plainText = String((draft && draft.plainText) || '');
  const applied = [];
  const skipped = [];

  for (const [token, raw] of Object.entries(values || {})) {
    const value = String(raw == null ? '' : raw).trim();
    if (!value) continue;
    if (html.includes(token)) {
      // Unwraps the .doc-ph highlight too — a filled value must not keep
      // showing as an outstanding placeholder.
      html = fillPlaceholderInHtml(html, token, esc(value));
      applied.push(token);
    } else {
      // Answered, but the document has no such token — worth reporting rather
      // than dropping, because the user typed it expecting it to land.
      skipped.push(token);
    }
    plainText = plainText.split(token).join(value);
  }

  return { html, plainText, applied, skipped };
}
